import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MapPin, Loader2, CheckCircle2, ImagePlus, X, CalendarIcon, Clock } from "lucide-react";
import AddressPinInput from "@/components/AddressPinInput";

const MS_PER_MINUTE = 60_000;
const DEFAULT_START_OFFSET_MIN = 30;
const DEFAULT_END_OFFSET_MIN = 90;

// ---------------------------------------------------------------------------
// Datetime shape helpers.
// Wire format for <input type="datetime-local"> and our form state is the
// browser-native "YYYY-MM-DDTHH:mm" string. We keep this unchanged so the
// submit path + auto-pair logic stay identical — only the *presentation*
// layer changes to a Calendar+Select combo.
// ---------------------------------------------------------------------------
function toLocalDatetimeInput(offsetMin) {
  const d = new Date(Date.now() + offsetMin * MS_PER_MINUTE);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Parse "YYYY-MM-DDTHH:mm" back into a JS Date without timezone drift.
function parseLocalDatetimeInput(s) {
  if (!s) return null;
  const [datePart, timePart = "00:00"] = s.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0);
  return isNaN(dt) ? null : dt;
}

// Build the 96 quarter-hour options: "12:00 AM" through "11:45 PM".
// Precomputed once at module load — 96 tiny objects, no runtime cost.
const QUARTER_HOUR_OPTIONS = (() => {
  const list = [];
  for (let h = 0; h < 24; h++) {
    for (const min of [0, 15, 30, 45]) {
      const hh12 = ((h + 11) % 12) + 1;
      const suffix = h < 12 ? "AM" : "PM";
      list.push({
        value: `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`, // "14:30"
        label: `${hh12}:${String(min).padStart(2, "0")} ${suffix}`,             // "2:30 PM"
      });
    }
  }
  return list;
})();

// Round any datetime string down to the nearest 15-min slot so opening the
// form pre-selects a real dropdown option rather than an unlisted minute
// like 14:37 (which would render as empty select value).
function snapTo15(dtStr) {
  const dt = parseLocalDatetimeInput(dtStr);
  if (!dt) return dtStr;
  dt.setMinutes(Math.floor(dt.getMinutes() / 15) * 15, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

export default function NewOpening() {
  const navigate = useNavigate();
  const [serviceTypes, setServiceTypes] = useState([]);
  const [form, setForm] = useState({
    service_type: "",
    title: "",
    description: "",
    address: "",
    start_time: snapTo15(toLocalDatetimeInput(DEFAULT_START_OFFSET_MIN)),
    end_time: snapTo15(toLocalDatetimeInput(DEFAULT_END_OFFSET_MIN)),
    price: "",
  });
  const [coords, setCoords] = useState(null); // { lat, lng, display_name }
  const [busy, setBusy] = useState(false);
  const [images, setImages] = useState([]); // [{id, url}]
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Pre-fill photos from the last opening this business posted so they
  // don't re-upload every time. Silent no-op on failure — a missing
  // gallery must never block posting.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/uploads/defaults");
        if (data?.files?.length) setImages(data.files);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const list = Array.from(fileList).slice(0, 3 - images.length);
    if (list.length === 0) {
      toast.error("Max 3 photos per opening");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      list.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImages((cur) => [...cur, ...data.files]);
      toast.success(`${data.files.length} photo(s) added`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (id) =>
    setImages((cur) => cur.filter((i) => i.id !== id));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/service-types");
        setServiceTypes(data.types);
        setForm((f) => ({ ...f, service_type: data.types[0] }));
      } catch (err) {
        // Silent in prod; dev-only diagnostic.
        if (process.env.NODE_ENV !== "production") {
          console.warn("[NewOpening] failed to load service types", err?.message || err); // eslint-disable-line no-console
        }
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fetch service types once on mount

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Auto-pair the End Time with Start Time + 1 hour whenever the business
  // picks a Start. This DOESN'T lock the End Time — businesses can still
  // adjust it after (e.g. for a 2-hour balayage, or a slot that crosses
  // midnight). Behaviour:
  //   • Start Time changed → End Time recalculated to Start + 1h, matching
  //     the same date so no manual re-picking of the date is needed.
  //   • If the user has already manually customised End Time to a value
  //     that ISN'T Start + 1h AND leaves at least 15 min for the slot, we
  //     preserve their choice (keep the delta) so bumping Start by 15 min
  //     doesn't wipe their intentional 90-min slot.
  //   • Empty / invalid Start Time → End Time unchanged.
  //
  // Called with the raw datetime-local string ("YYYY-MM-DDTHH:mm") from the
  // custom SlotPicker (NOT a synthetic event) — same shape as the previous
  // native input handler stripped of its `.target.value` indirection.
  const updStartTime = (nextStart) => {
    setForm((f) => {
      // Preserve user's chosen slot length if it's non-default.
      const parsedPrev = f.start_time ? parseLocalDatetimeInput(f.start_time) : null;
      const parsedPrevEnd = f.end_time ? parseLocalDatetimeInput(f.end_time) : null;
      const prevDeltaMs =
        parsedPrev && parsedPrevEnd
          ? parsedPrevEnd.getTime() - parsedPrev.getTime()
          : 60 * 60 * 1000; // fallback: 1 hour
      const useDelta = prevDeltaMs >= 15 * 60 * 1000 ? prevDeltaMs : 60 * 60 * 1000;

      const parsedNext = nextStart ? parseLocalDatetimeInput(nextStart) : null;
      if (!parsedNext) {
        return { ...f, start_time: nextStart };
      }
      const nextEnd = new Date(parsedNext.getTime() + useDelta);
      const pad = (n) => String(n).padStart(2, "0");
      const endStr =
        `${nextEnd.getFullYear()}-${pad(nextEnd.getMonth() + 1)}-${pad(nextEnd.getDate())}` +
        `T${pad(nextEnd.getHours())}:${pad(nextEnd.getMinutes())}`;
      return { ...f, start_time: nextStart, end_time: endStr };
    });
  };

  // Reset coords when address changes — handled inside AddressPinInput now,
  // but we still keep local `coords` state up here so submit() can gate on
  // "has the user pinned the current address?". AddressPinInput's `onPinned`
  // and `onCleared` callbacks keep this in sync.

  const submit = async (e) => {
    e.preventDefault();
    if (!coords) {
      toast.error("Please pin the address on the map first");
      return;
    }
    const price = form.price.trim();
    if (!price) {
      toast.error("Please add a price so customers know what to expect");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        service_type: form.service_type,
        title: form.title,
        description: form.description,
        address: form.address,
        lat: coords.lat,
        lng: coords.lng,
        start_time: new Date(form.start_time).toISOString(),
        end_time: new Date(form.end_time).toISOString(),
        price,
        image_ids: images.map((i) => i.id),
      };
      await api.post("/openings", payload);
      toast.success("Opening posted — now live on the map.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl p-4 md:p-8">
      <div className="border border-border">
        <div className="border-b border-border p-6 md:p-8">
          <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
            → new opening
          </p>
          <h1 className="mt-2 text-4xl leading-tight tracking-tight">
            Post an instant slot.
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            It goes on the map immediately. Consumers nearby will see it and can
            request to book.
          </p>
        </div>

        <form onSubmit={submit} className="grid gap-5 p-6 md:p-8">
          <div className="grid gap-1.5">
            <Label className="mono text-[10px] uppercase tracking-[0.2em]">
              Service type
            </Label>
            <Select
              value={form.service_type}
              onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}
            >
              <SelectTrigger data-testid="new-service-type-select" className="rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {serviceTypes.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label className="mono text-[10px] uppercase tracking-[0.2em]">Title *</Label>
            <Input
              data-testid="new-title-input"
              required
              value={form.title}
              onChange={upd("title")}
              placeholder="Walk-in haircut slot"
              className="rounded-none h-11 md:h-10"
              autoComplete="off"
              autoCapitalize="sentences"
              enterKeyHint="next"
            />
          </div>

          <div className="grid gap-1.5">
            <Label className="mono text-[10px] uppercase tracking-[0.2em]">Description</Label>
            <Textarea
              data-testid="new-description-input"
              value={form.description}
              onChange={upd("description")}
              rows={3}
              className="rounded-none resize-none"
              placeholder="Something short and enticing"
              autoCapitalize="sentences"
            />
          </div>

          <div className="grid gap-1.5">
            <Label className="mono text-[10px] uppercase tracking-[0.2em]">
              Address / venue *
            </Label>
            <AddressPinInput
              testIdPrefix="new-address"
              value={form.address}
              onChange={(v) => setForm((f) => ({ ...f, address: v }))}
              onPinned={setCoords}
              onCleared={() => setCoords(null)}
              required
              placeholder="123 Market St, San Francisco"
            />
          </div>

          <div className="grid gap-1.5">
            <Label className="mono text-[10px] uppercase tracking-[0.2em]">
              Photos (optional, max 3)
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {images.map((img) => (
                <div
                  key={img.id}
                  data-testid={`opening-image-${img.id}`}
                  className="relative aspect-square border border-border overflow-hidden group"
                >
                  <img
                    src={img.url}
                    alt="Opening photo"
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(img.id)}
                    aria-label="Remove photo"
                    className="absolute top-1 right-1 grid h-6 w-6 place-items-center bg-foreground text-background opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-[#C9A85F] transition-opacity"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              {images.length < 3 && (
                <button
                  type="button"
                  data-testid="upload-images-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="grid aspect-square place-items-center border border-dashed border-border text-muted-foreground hover:border-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-[#C9A85F] transition-colors disabled:opacity-50"
                >
                  {uploading ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <div className="flex flex-col items-center gap-1">
                      <ImagePlus size={20} />
                      <span className="mono text-[9px] uppercase tracking-widest">
                        Add photo
                      </span>
                    </div>
                  )}
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              data-testid="upload-images-input"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
            />
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <SlotPicker
              label="Start time"
              testIdBase="new-start-time"
              value={form.start_time}
              onChange={updStartTime}
            />
            <div>
              <SlotPicker
                label="End time"
                testIdBase="new-end-time"
                value={form.end_time}
                onChange={(v) => setForm((f) => ({ ...f, end_time: v }))}
              />
              <p className="mono mt-1.5 text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                Auto-set to +1h. Adjust for longer slots or midnight crossovers.
              </p>
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label className="mono text-[10px] uppercase tracking-[0.2em]">
              Price *
            </Label>
            <Input
              data-testid="new-price-input"
              type="text"
              required
              maxLength={32}
              value={form.price}
              onChange={upd("price")}
              placeholder="e.g. $45 or From $50"
              className="rounded-none h-11 md:h-10"
              enterKeyHint="done"
            />
            <p className="mono text-[9px] uppercase tracking-widest text-muted-foreground">
              Free text — enter an exact price or a range like "From $50".
            </p>
          </div>

          {/* Sticky CTA on mobile so thumb-reach stays comfortable while scrolling
              through the form. Reverts to inline on ≥md. */}
          <div className="mt-2 sticky bottom-0 -mx-6 md:mx-0 md:static bg-background/95 backdrop-blur-md border-t border-border md:border-0 px-6 md:px-0 py-3 md:py-0 flex gap-3 safe-bottom z-10">
            <button
              type="button"
              data-testid="new-cancel-btn"
              onClick={() => navigate("/dashboard")}
              className="h-11 md:h-10 border border-border px-4 text-xs uppercase tracking-widest hover:border-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="new-submit-btn"
              disabled={busy || !coords}
              className="h-11 md:h-10 flex-1 gold-metallic-bg px-4 text-xs font-semibold uppercase tracking-widest text-black active:scale-[0.98] transition-transform disabled:opacity-60"
            >
              {busy ? "Posting…" : "Post opening"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// SlotPicker — a two-field replacement for `<input type="datetime-local">`
// that gives us full control of the palette and forces 15-min interval.
//
// Two composed controls, one form value:
//   • Date field → shadcn Popover + Calendar (champagne-gold selected state)
//   • Time field → shadcn Select with 96 quarter-hour options
//
// Emits the same "YYYY-MM-DDTHH:mm" string the parent expects — so all
// downstream code (auto-pair, submit) stays identical.
// ---------------------------------------------------------------------------
function SlotPicker({ label, testIdBase, value, onChange }) {
  const parsed = parseLocalDatetimeInput(value);
  const dateStr = parsed
    ? parsed.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })
    : "Pick a date";
  const pad = (n) => String(n).padStart(2, "0");
  const currentTime = parsed ? `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}` : "";

  const setDate = (newDate) => {
    if (!newDate) return;
    // Preserve the existing HH:mm when the user only changes the calendar.
    const hh = parsed ? parsed.getHours() : 9;
    const mm = parsed ? parsed.getMinutes() : 0;
    const out =
      `${newDate.getFullYear()}-${pad(newDate.getMonth() + 1)}-${pad(newDate.getDate())}` +
      `T${pad(hh)}:${pad(mm)}`;
    onChange(out);
  };
  const setTime = (hhmm) => {
    // Preserve the existing date when the user only changes the time.
    const base = parsed || new Date();
    const [hh, mm] = hhmm.split(":").map(Number);
    const out =
      `${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}` +
      `T${pad(hh)}:${pad(mm)}`;
    onChange(out);
  };

  return (
    <div className="grid gap-1.5">
      <Label className="mono text-[10px] uppercase tracking-[0.2em]">{label}</Label>
      <div className="grid grid-cols-[1fr_auto] gap-2">
        {/* Date — Popover + shadcn Calendar in gold/cream */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={`${testIdBase}-date-btn`}
              className="inline-flex h-11 md:h-10 items-center gap-2 border border-border bg-background px-3 text-left text-sm hover:border-[#C9A85F] focus-visible:border-[#C9A85F] focus-visible:ring-2 focus-visible:ring-[#C9A85F]/40 transition-colors"
            >
              <CalendarIcon size={14} className="text-[#C9A85F]" />
              <span className="truncate">{dateStr}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            data-testid={`${testIdBase}-date-popover`}
            align="start"
            className="w-auto p-0 border-[#C9A85F]/40"
          >
            <Calendar
              mode="single"
              selected={parsed || undefined}
              onSelect={setDate}
              initialFocus
              classNames={{
                // Preserve shadcn's `w-8 h-8` sizing on every cell so the
                // grid keeps its 7-column layout — overriding these keys
                // with color-only strings collapses the calendar into a
                // spaceless clump. We APPEND colours, not replace.
                head_cell:
                  "text-[#8A7333] rounded-md w-9 font-medium text-[0.8rem]",
                row: "flex w-full mt-2",
                cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:rounded-md",
                day: "h-9 w-9 p-0 font-normal rounded-md hover:bg-[#F7F5F2] hover:text-[#2A2724] aria-selected:opacity-100 inline-flex items-center justify-center transition-colors",
                day_selected:
                  "bg-[#C9A85F] text-black hover:bg-[#C9A85F] hover:text-black focus:bg-[#C9A85F] focus:text-black",
                day_today:
                  "bg-[#C9A85F]/15 text-[#8A7333] font-semibold",
                day_outside:
                  "text-muted-foreground/50 aria-selected:bg-[#C9A85F]/30",
                caption_label: "text-sm font-medium tracking-tight",
                nav_button:
                  "h-7 w-7 bg-transparent p-0 opacity-60 hover:opacity-100 hover:bg-[#C9A85F]/10 hover:text-[#8A7333] rounded-md inline-flex items-center justify-center border border-border",
              }}
            />
          </PopoverContent>
        </Popover>

        {/* Time — 15-min interval shadcn Select */}
        <Select value={currentTime} onValueChange={setTime}>
          <SelectTrigger
            data-testid={`${testIdBase}-time-select`}
            className="h-11 md:h-10 w-[120px] rounded-none border-border hover:border-[#C9A85F] focus:border-[#C9A85F] focus:ring-2 focus:ring-[#C9A85F]/40 gap-2"
          >
            <Clock size={14} className="text-[#C9A85F] shrink-0" />
            <SelectValue placeholder="Time" />
          </SelectTrigger>
          <SelectContent
            data-testid={`${testIdBase}-time-list`}
            className="max-h-[280px] border-[#C9A85F]/40"
          >
            {QUARTER_HOUR_OPTIONS.map((opt) => (
              <SelectItem
                key={opt.value}
                value={opt.value}
                data-testid={`${testIdBase}-time-opt-${opt.value.replace(":", "")}`}
                className="focus:bg-[#C9A85F]/10 focus:text-[#8A7333] data-[state=checked]:bg-[#C9A85F] data-[state=checked]:text-black"
              >
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

